import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useSignedUrl } from "@/hooks/useSignedUrl";

interface Media {
  id: string;
  title: string;
  description: string | null;
  media_type: string;
  image_url: string;
  video_url: string | null;
  display_order: number;
  course_id: string | null;
  courses?: {
    title: string;
  };
}

interface BibleSchoolLightboxProps {
  media: Media;
  title: string;
  description: string | null;
  canGoPrevious: boolean;
  canGoNext: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
}

const getYouTubeVideoId = (url: string): string => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : "";
};

const getYouTubeEmbedUrl = (url: string): string => {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
};

export const BibleSchoolLightbox = ({
  media,
  title,
  description,
  canGoPrevious,
  canGoNext,
  onClose,
  onPrevious,
  onNext,
}: BibleSchoolLightboxProps) => {
  const { signedUrl, loading } = useSignedUrl('mission-photos', media.image_url);

  return (
    <div className="relative bg-background">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 z-10"
        onClick={onClose}
      >
        <X className="h-6 w-6" />
      </Button>

      {media.media_type === "video" && media.video_url ? (
        <div className="aspect-video">
          <iframe
            src={getYouTubeEmbedUrl(media.video_url)}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : loading ? (
        <div className="w-full h-[80vh] bg-muted animate-pulse" />
      ) : (
        <img
          src={signedUrl}
          alt={title}
          className="w-full h-auto max-h-[80vh] object-contain"
        />
      )}

      <div className="p-6">
        <h3 className="text-2xl font-bold mb-2">{title}</h3>
        {description && (
          <p className="text-muted-foreground mb-4">{description}</p>
        )}
        {media.courses && (
          <p className="text-primary font-medium">
            Related Course: {media.courses.title}
          </p>
        )}
      </div>

      {canGoPrevious && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-2 top-1/2 -translate-y-1/2"
          onClick={onPrevious}
        >
          <ChevronLeft className="h-8 w-8" />
        </Button>
      )}

      {canGoNext && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-1/2 -translate-y-1/2"
          onClick={onNext}
        >
          <ChevronRight className="h-8 w-8" />
        </Button>
      )}
    </div>
  );
};
